import React from "react";
import { Button, Tooltip } from "antd";
import { SendOutlined, EyeOutlined, InfoCircleOutlined, UnorderedListOutlined, TeamOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ACTION_TYPES } from "./constants";
import "./PendingActions.less";

const getCardClass = (type) => {
  const classMap = {
    [ACTION_TYPES.M3P_ELIGIBLE]: "action-card-m3p",
    [ACTION_TYPES.NEW_PROGRAMS]: "action-card-programs",
    [ACTION_TYPES.PENDING_RENEWALS]: "action-card-renewals",
    [ACTION_TYPES.OVERDUE_TASKS]: "action-card-tasks"
  };
  return classMap[type] || "";
};

/**
 * Auto-Approval Prompt - replaces the buttons section after user sends invites
 */
const AutoApprovalPrompt = ({ onEnable, onDismiss, isLoading, t }) => {
  return (
    <div className="auto-approval-prompt">
      <div className="auto-approval-prompt-content">
        <div className="auto-approval-prompt-icon-wrapper">
          <TeamOutlined className="auto-approval-prompt-icon" />
        </div>
        <div className="auto-approval-prompt-text">
          <div className="auto-approval-prompt-title">
            {t("auto_approval.banner.title", "Save time with auto-invites?")}
          </div>
          <div className="auto-approval-prompt-description">
            {t("auto_approval.banner.description", "New eligible patients will receive invites automatically.")}
          </div>
        </div>
      </div>
      <div className="auto-approval-prompt-actions">
        <Button type="text" onClick={onDismiss} disabled={isLoading} className="auto-approval-prompt-btn-dismiss">
          {t("auto_approval.banner.not_now", "Not now")}
        </Button>
        <Button type="primary" onClick={onEnable} loading={isLoading} className="auto-approval-prompt-btn-enable">
          {t("auto_approval.banner.enable", "Enable")}
        </Button>
      </div>
    </div>
  );
};

const ActionCard = ({
  action,
  onSendToAll,
  onReviewPatients,
  onViewTasks,
  disabled = false,
  showAutoApprovalBanner = false,
  onEnableAutoApproval,
  onDismissAutoApproval,
  isEnablingAutoApproval = false
}) => {
  const { t } = useTranslation();
  const { type, title, count, actionLabel, tooltip, countLabel } = action;

  const handleSendToAll = () => {
    if (!disabled) {
      onSendToAll?.(action);
    }
  };

  const handleReviewClick = () => {
    if (!disabled) {
      onReviewPatients?.(action);
    }
  };

  const handleViewTasks = (e) => {
    // Stop propagation to prevent the click-outside handler in Tasks.js from immediately closing the popup
    e.stopPropagation();
    if (!disabled) {
      onViewTasks?.(action);
    }
  };

  // Overdue tasks card has a simpler layout with just one button
  const isTasksCard = type === ACTION_TYPES.OVERDUE_TASKS;

  return (
    <div className={`action-card ${getCardClass(type)}${disabled ? " action-card-disabled" : ""}`}>
      <div className="action-card-header">
        <div className="action-card-title-row">
          <TeamOutlined className="action-card-title-icon" />
          <span className="action-card-title">{title}</span>
        </div>
        {tooltip && (
          <Tooltip title={tooltip} placement="top" overlayStyle={{ maxWidth: 320 }}>
            <InfoCircleOutlined className="action-card-info" />
          </Tooltip>
        )}
      </div>

      {/* Count section - always shown */}
      <div className="action-card-count-section">
        <Tooltip title={countLabel || `${count} ${t("patients").toLowerCase()}`}>
          <div className="action-card-count">{count}</div>
        </Tooltip>
        <div className="action-card-count-label">{t("pending_actions.patients_eligible", "Patients eligible")}</div>
      </div>

      {/* Show auto-approval prompt OR normal buttons */}
      {showAutoApprovalBanner ? (
        <AutoApprovalPrompt
          onEnable={onEnableAutoApproval}
          onDismiss={onDismissAutoApproval}
          isLoading={isEnablingAutoApproval}
          t={t}
        />
      ) : (
        <div className="action-card-buttons">
          {isTasksCard ? (
            <Button
              className="action-card-btn-primary action-card-btn-full"
              onClick={handleViewTasks}
              disabled={disabled}
            >
              <UnorderedListOutlined />
              {actionLabel}
            </Button>
          ) : (
            <>
              <Button className="action-card-btn-primary" onClick={handleSendToAll} disabled={disabled}>
                <SendOutlined />
                {t("pending_actions.send_invites", "Send Invites")}
              </Button>
              <Button className="action-card-btn-secondary" onClick={handleReviewClick} disabled={disabled}>
                <EyeOutlined />
                {t("pending_actions.review_patients", "Review Patients")}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ActionCard;
